import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// Helper to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Create a safe wrapper for useError that doesn't throw if ErrorContext is not available
let useErrorSafe = null;
try {
  const ErrorContextModule = require('./ErrorContext');
  useErrorSafe = () => {
    try {
      return ErrorContextModule.useError();
    } catch {
      return { showError: null };
    }
  };
} catch {
  useErrorSafe = () => ({ showError: null });
}

export const AuthProvider = ({ children }) => {
  // Get error context if available (ErrorProvider wraps AuthProvider in App.jsx)
  const errorContext = useErrorSafe();
  const showError = errorContext?.showError || null;
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Load user profile
        const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        
        if (profileError) {
          console.error('Profile load error:', profileError)
          // Don't throw - set a default profile structure to prevent crashes
          setUserProfile({
            id: userId,
            displayName: 'User',
            profileCompletion: 0,
            subscriptionStatus: 'free'
          })
          return
        }

        // Check for active subscription in stripe_subscriptions table
        // Handle gracefully if table doesn't exist
        let subscriptionData = null;
        try {
          const { data, error } = await supabase
            ?.from('stripe_subscriptions')
            ?.select('status')
            ?.eq('user_id', userId)
            ?.in('status', ['active', 'trialing'])
            ?.order('created_at', { ascending: false })
            ?.limit(1);
          
          // Handle errors gracefully (404 = table not found, 406 = no rows/RLS blocking, PGRST116 = no rows)
          if (!error) {
            // Got data - use first item if exists
            if (data && data.length > 0) {
              subscriptionData = data[0];
            }
          } else if (error?.code === 'PGRST116' || error?.status === 404 || error?.status === 406) {
            // No subscription found or table doesn't exist - that's OK
            subscriptionData = null;
          } else {
            // Other error - log but continue
            console.warn('Error checking subscription:', error);
          }
        } catch (err) {
          // Table doesn't exist - continue without subscription check
          console.warn('stripe_subscriptions table not found, using user_profiles subscription_status only');
        }

        // Merge profile data with subscription status
        const mergedProfile = {
          ...profileData,
          // Update subscription_status based on stripe_subscriptions if active subscription exists
          subscription_status: subscriptionData?.status === 'active' || subscriptionData?.status === 'trialing' 
            ? 'premium' 
            : (profileData?.subscription_status || 'free')
        }

        // Convert snake_case to camelCase for consistency
        setUserProfile(toCamelCase(mergedProfile))
      } catch (error) {
        console.error('Profile load error:', error)
        // Set fallback profile to prevent app crashes
        setUserProfile({
          id: userId,
          displayName: 'User',
          profileCompletion: 0,
          subscriptionStatus: 'free'
        })
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      // Map Supabase errors to user-friendly messages
      if (error) {
        let userFriendlyMessage = error?.message || 'An error occurred during sign in'
        
        if (error?.message?.includes('Invalid login credentials') || error?.message?.includes('Invalid credentials')) {
          userFriendlyMessage = 'Email or password is incorrect'
        } else if (error?.message?.includes('Email not confirmed') || error?.message?.includes('email_not_confirmed')) {
          userFriendlyMessage = 'Please check your email and confirm your account before signing in'
        } else if (error?.message?.includes('User not found') || error?.message?.includes('user_not_found')) {
          userFriendlyMessage = 'No account found with this email. Please create an account first'
        }
        
        // Show error notification if available
        if (showError) {
          showError(userFriendlyMessage);
        }
        
        return { 
          data: null, 
          error: { 
            ...error, 
            message: userFriendlyMessage 
          } 
        }
      }
      
      return { data, error: null }
    } catch (error) {
      const errorMessage = 'Network error. Please try again.';
      if (showError) {
        showError(errorMessage);
      }
      return { error: { message: errorMessage } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      } else if (showError) {
        showError('Failed to sign out. Please try again.');
      }
      return { error }
    } catch (error) {
      const errorMessage = 'Network error. Please try again.';
      if (showError) {
        showError(errorMessage);
      }
      return { error: { message: errorMessage } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) {
      const errorMessage = 'No user logged in';
      if (showError) {
        showError(errorMessage);
      }
      return { error: { message: errorMessage } }
    }
    
    try {
      // Convert camelCase updates to snake_case for database
      const snakeUpdates = Object.keys(updates).reduce((acc, key) => {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        acc[snakeKey] = updates[key];
        return acc;
      }, {});
      
      const { data, error } = await supabase?.from('user_profiles')?.update(snakeUpdates)?.eq('id', user?.id)?.select()?.single()
      if (error) {
        const errorMessage = error?.message || 'Failed to update profile. Please try again.';
        if (showError) {
          showError(errorMessage);
        }
        return { data: null, error: { ...error, message: errorMessage } }
      }
      // Convert response back to camelCase
      setUserProfile(toCamelCase(data))
      return { data: toCamelCase(data), error: null }
    } catch (error) {
      const errorMessage = 'Network error. Please try again.';
      if (showError) {
        showError(errorMessage);
      }
      return { error: { message: errorMessage } }
    }
  }

  const refreshProfile = () => {
    if (user?.id) profileOperations?.load(user?.id)
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
